import React, { useState } from 'react';

import { sanitize } from 'lib/helpers/sanitize';
import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import { formatLabel } from 'components/shared/ExpandableToolbar/lib/helpers';

import { formatChangedValues } from '../lib/formatChangedValues';

const MAX_VISIBLE_ITEMS = 2;
const VALUE_ONLY_KEYS = [
  'added_service',
  'added_services',
  'added_product',
  'added_products',
  'removed_service',
  'removed_services',
  'removed_product',
  'removed_products',
  'updated_service',
  'added_tag',
  'added_tags',
  'removed_tag',
  'removed_tags'
];

const ActivityContent = ({
  isChild = false,
  formattedChanges = [],
  action,
  overwriteActivities = {},
  fieldLabels = {}
}) => {
  const isDelete = action === 'delete';
  const isUpload = action === 'upload';
  const isAttachment = isDelete || isUpload;

  const [showAll, setShowAll] = useState(false);

  const hasMoreItems = formattedChanges.length > MAX_VISIBLE_ITEMS;
  const visibleChanges =
    showAll || !hasMoreItems ? formattedChanges : formattedChanges.slice(0, MAX_VISIBLE_ITEMS);

  return (
    <div className={cn(!isChild && 'pl-[9px]')}>
      <div
        className={cn(
          'flex flex-col gap-2 border-0 border-l border-solid border-neutral-300',
          isChild && 'flex-1'
        )}>
        {visibleChanges.map((item) => {
          const { key, isNestedKey, prevValueRaw, currValueRaw, prevValue, currValue } = item;

          const itemClassName =
            'flex flex-wrap items-center gap-1 pl-[13px] text-sm leading-[22px] text-neutral-600';

          if (Object.keys(overwriteActivities).includes(key)) {
            return (
              <p
                key={key}
                className="flex flex-wrap gap-1 pl-[13px] text-sm leading-[22px] text-neutral-700">
                {overwriteActivities[key]}
              </p>
            );
          }

          return (
            <div key={key} className={itemClassName}>
              <span className="font-semibold text-neutral-800">
                {fieldLabels[key] || formatLabel(key)}
              </span>

              {isNestedKey ? (
                <ActivityContent
                  key={key}
                  isChild
                  action={action}
                  formattedChanges={
                    formatChangedValues({
                      activities: [{ previousValues: prevValueRaw, currentValues: currValueRaw }]
                    })[0]?.formattedChanges || []
                  }
                />
              ) : VALUE_ONLY_KEYS.includes(key) ? (
                <span
                  className="font-semibold text-neutral-800"
                  dangerouslySetInnerHTML={sanitize(
                    currValue && currValue !== 'blank' ? currValue : prevValue
                  )}
                />
              ) : (
                <>
                  {!isAttachment && <span>from</span>}

                  {(!isAttachment || isDelete) && (
                    <span dangerouslySetInnerHTML={sanitize(prevValue)} />
                  )}

                  {(!isAttachment || isUpload) && (
                    <>
                      {!isAttachment && <span>to</span>}
                      <span
                        className="font-semibold text-neutral-800"
                        dangerouslySetInnerHTML={sanitize(currValue)}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}

        {hasMoreItems && (
          <div className="pl-[13px]">
            <Button
              size="small"
              variant="link"
              text={`Show ${showAll ? 'less' : 'all...'}`}
              onClick={() => setShowAll((prev) => !prev)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityContent;
